import { Component, OnInit } from '@angular/core';
import { Priority } from '@app/shared/constants/global.enum';
import { forkJoin } from 'rxjs';
import {
  ActiveUser,
  AdoptionService,
  AdoptionSummary,
  AdoptionTrendPoint,
  FeatureUsage,
  PersonaUsage,
} from '../adoption.service';

interface DateRangeOption {
  label: string;
  days: number;
  value: string;
  selected?: boolean;
}

const PERSONA_COLORS = [
  '#003F2D', '#2563eb', '#d97706', '#dc2626', '#7c3aed', '#0891b2',
  '#65a30d', '#6b7280', '#0369a1', '#9333ea', '#ea580c', '#15803d',
  '#0d9488', '#be123c', '#4338ca', '#b45309',
];

const FEATURE_PALETTE = [
  '#003F2D', '#2563eb', '#d97706', '#7c3aed', '#0891b2', '#65a30d',
  '#dc2626', '#0369a1', '#a16207', '#15803d', '#9333ea', '#ea580c',
  '#0d9488', '#be123c', '#4338ca', '#b45309',
];

@Component({
  selector: 'app-adoption-page',
  templateUrl: './adoption-page.component.html',
  styleUrls: ['./adoption-page.component.scss'],
})
export class AdoptionPageComponent implements OnInit {
  priority = Priority;
  loading = false;
  loadError = false;

  rangeOptions: DateRangeOption[] = [
    { label: 'Last 7 days', days: 7, value: '7', selected: false },
    { label: 'Last 30 days', days: 30, value: '30', selected: true },
    { label: 'Last 90 days', days: 90, value: '90', selected: false },
    { label: 'Last 180 days', days: 180, value: '180', selected: false },
  ];
  selectedRangeDays = 30;

  summary: AdoptionSummary | null = null;
  features: FeatureUsage[] = [];
  personas: PersonaUsage[] = [];
  trend: AdoptionTrendPoint[] = [];
  activeUsers: ActiveUser[] = [];
  activeUsersLimit = 50;
  activeUsersHasMore = false;
  activeUsersLoadingMore = false;
  private static readonly ACTIVE_USERS_BATCH = 50;

  featureChartData: any = null;
  featureChartOptions: any = null;
  personaChartData: any = null;
  personaChartOptions: any = null;
  trendChartData: any = null;
  trendChartOptions: any = null;

  constructor(private readonly adoptionService: AdoptionService) {}

  ngOnInit(): void {
    this.featureChartOptions = this.buildBarOptions();
    this.personaChartOptions = this.buildDonutOptions();
    this.trendChartOptions = this.buildLineOptions();
    this.fetchAll();
  }

  onRangeChange(event: any): void {
    const label = typeof event === 'string' ? event : event?.value;
    const option = this.rangeOptions.find((o) => o.label === label || o.value === label);
    if (!option) return;
    this.selectedRangeDays = option.days;
    this.activeUsersLimit = AdoptionPageComponent.ACTIVE_USERS_BATCH;
    this.fetchAll();
  }

  showMoreUsers(): void {
    const { from, to } = this.computeRange(this.selectedRangeDays);
    const nextLimit = this.activeUsersLimit + AdoptionPageComponent.ACTIVE_USERS_BATCH;
    this.activeUsersLoadingMore = true;
    this.adoptionService.getActiveUsers(from, to, nextLimit).subscribe({
      next: (rows) => {
        const list = rows ?? [];
        this.activeUsers = list;
        this.activeUsersLimit = nextLimit;
        this.activeUsersHasMore = list.length >= nextLimit;
        this.activeUsersLoadingMore = false;
      },
      error: () => {
        this.activeUsersLoadingMore = false;
      },
    });
  }

  private fetchAll(): void {
    const { from, to } = this.computeRange(this.selectedRangeDays);
    this.loading = true;
    this.loadError = false;

    forkJoin({
      summary: this.adoptionService.getSummary(from, to),
      features: this.adoptionService.getByFeature(from, to, 12),
      personas: this.adoptionService.getByPersona(from, to),
      trend: this.adoptionService.getTrend(from, to, 'daily'),
      activeUsers: this.adoptionService.getActiveUsers(from, to, this.activeUsersLimit),
    }).subscribe({
      next: (res) => {
        this.summary = res.summary;
        this.features = res.features ?? [];
        this.personas = res.personas ?? [];
        this.trend = res.trend ?? [];
        this.activeUsers = res.activeUsers ?? [];
        this.activeUsersHasMore = this.activeUsers.length >= this.activeUsersLimit;
        this.renderCharts();
        this.loading = false;
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  private renderCharts(): void {
    // Top features — horizontal bar; per-bar colors from palette
    this.featureChartData = {
      labels: this.features.map((f) => f.featureKey),
      datasets: [
        {
          label: 'Events',
          backgroundColor: this.features.map((_, i) => FEATURE_PALETTE[i % FEATURE_PALETTE.length]),
          data: this.features.map((f) => f.eventCount),
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 22,
        },
      ],
    };

    // Personas — donut
    this.personaChartData = {
      labels: this.personas.map((p) => p.persona),
      datasets: [
        {
          data: this.personas.map((p) => p.eventCount),
          backgroundColor: this.personas.map((_, i) => PERSONA_COLORS[i % PERSONA_COLORS.length]),
          borderWidth: 0,
        },
      ],
    };

    // Trend — line
    this.trendChartData = {
      labels: this.trend.map((p) => p.bucket),
      datasets: [
        {
          label: 'Active users',
          data: this.trend.map((p) => p.activeUsers),
          borderColor: '#003F2D',
          backgroundColor: 'rgba(0, 63, 45, 0.15)',
          tension: 0.3,
          fill: true,
          pointRadius: 3,
        },
        {
          label: 'Events',
          data: this.trend.map((p) => p.events),
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          tension: 0.3,
          fill: false,
          pointRadius: 2,
        },
      ],
    };
  }

  private computeRange(days: number): { from: string; to: string } {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
    return { from: this.toIso(from), to: this.toIso(today) };
  }

  private toIso(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private buildBarOptions(): any {
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: { mode: 'index', intersect: false },
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 } }, grid: { color: '#f3f4f6' } },
        y: { ticks: { font: { size: 11 } }, grid: { display: false } },
      },
    };
  }

  private buildDonutOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '60%',
      plugins: {
        legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 10, padding: 10 } },
        datalabels: { display: false },
      },
    };
  }

  private buildLineOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12, padding: 12 } },
        datalabels: { display: false },
        tooltip: { mode: 'index', intersect: false },
      },
      scales: {
        x: { ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true }, grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: '#f3f4f6' } },
      },
    };
  }
}
